import { apiCall, API_CONFIG } from './api';
import { formatDateOnly } from '../utils/utils';

const DISPATCH = API_CONFIG.ENDPOINTS.DISPATCH;
const ORDERS = API_CONFIG.ENDPOINTS.ORDERS;

export const dispatchAPI = {
  getConfirmedOrders: async (date = null, timeSlot = null) => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', formatDateOnly(date));
      if (timeSlot && timeSlot !== 'ALL ORDERS') {
        params.append('timeSlot', timeSlot);
      }
      return await apiCall(`${ORDERS}/confirmed?${params.toString()}`, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch confirmed orders:', error);
      throw error;
    }
  },

  getDispatchDetails: async (date = null) => {
    try {
      let url = `${DISPATCH}/fetch`;
      if (date) url += `?date=${formatDateOnly(date)}`;
      return await apiCall(url, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch dispatch details:', error);
      throw error;
    }
  },

  fetchMeta: async (date = null) => dispatchAPI.getDispatchDetails(date),

  planRoutes: async (payload) => {
    try {
      const body = payload?.date
        ? { ...payload, date: formatDateOnly(payload.date) }
        : payload;
      return await apiCall(`${DISPATCH}/plan`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error('Failed to plan routes:', error);
      return {
        success: false,
        msg: error.message || 'Failed to plan routes',
        data: null,
      };
    }
  },

  checkUsedOrdersAndRoute: async (currentSlot, currentDate) => {
    try {
      const response = await apiCall(
        `${DISPATCH}/route-history?slot=${encodeURIComponent(currentSlot)}&date=${formatDateOnly(currentDate)}`,
        { method: 'GET' }
      );

      const routes = response.success
        ? (response.data || [])
        : (Array.isArray(response) ? response : []);
      const usedOrderSet = new Set();
      let existingRoute = null;

      if (Array.isArray(routes) && routes.length > 0) {
        existingRoute = routes[0];
        routes.forEach((route) => {
          (route.plans || []).forEach((plan) => {
            (plan.stops || []).forEach((stop) => {
              if (stop.order) usedOrderSet.add(stop.order);
            });
          });
        });
      }

      return { usedOrderSet, existingRoute };
    } catch (error) {
      console.error('Error checking used orders and route:', error);
      return { usedOrderSet: new Set(), existingRoute: null };
    }
  },

  saveRouteToHistory: async (plans, timeSlot, date, slotId = null) => {
    try {
      return await apiCall(`${DISPATCH}/route-history`, {
        method: 'POST',
        body: JSON.stringify({
          slot: timeSlot,
          date: formatDateOnly(date),
          plans,
          ...(slotId ? { slotId } : {}),
        }),
      });
    } catch (error) {
      console.error('Error saving route to history:', error);
      throw error;
    }
  },

  saveRouteHistory: async (payload) => {
    const { plans, slot, date, slotId } = payload || {};
    return dispatchAPI.saveRouteToHistory(plans, slot, date, slotId);
  },

  updateRouteInHistory: async (routeId, plans, timeSlot, date, slotId = null) => {
    try {
      return await apiCall(`${DISPATCH}/route-history/${routeId}`, {
        method: 'PUT',
        body: JSON.stringify({
          plans,
          slot: timeSlot,
          date: formatDateOnly(date),
          ...(slotId ? { slotId } : {}),
        }),
      });
    } catch (error) {
      console.error('Error updating route in history:', error);
      throw error;
    }
  },

  deleteRouteHistory: async (routeId) => {
    try {
      return await apiCall(`${DISPATCH}/history/${routeId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Error deleting route from history:', error);
      throw error;
    }
  },

  getRouteOverview: async (driver, date, slot, routeId = null) => {
    try {
      const params = new URLSearchParams();
      if (driver) params.append('driver', driver);
      if (date) params.append('date', formatDateOnly(date));
      if (slot) params.append('slot', slot);
      if (routeId) params.append('routeId', routeId);
      return await apiCall(`${DISPATCH}/route-overview?${params.toString()}`, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch route overview:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch route overview',
        data: null,
      };
    }
  },

  getDispatchHistory: async (page = 1, limit = 20, filterType = 'today', startDate = null, slot = null, id = null) => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      params.append('filterType', filterType);
      if (startDate) params.append('startDate', formatDateOnly(startDate));
      if (slot && slot !== 'ALL') params.append('slot', slot);
      if (id) params.append('id', id);
      return await apiCall(`${DISPATCH}/history?${params.toString()}`, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch dispatch history:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch dispatch history',
        data: { routes: [], pagination: {} },
      };
    }
  },

  getDispatchHistorySlots: async (filterType = 'today', startDate = null) => {
    try {
      const params = new URLSearchParams();
      params.append('filterType', filterType);
      if (startDate) params.append('startDate', formatDateOnly(startDate));
      return await apiCall(`${DISPATCH}/history/slots?${params.toString()}`, { method: 'GET' });
    } catch (error) {
      console.error('Failed to fetch dispatch history slots:', error);
      return { success: false, data: [] };
    }
  },
};
